import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOM_FIELD_MODULE_DEFINITIONS,
  type AddCustomFieldInput,
  type CustomFieldConfiguration,
  type CustomFieldModule,
  type ReorderCustomFieldsInput,
  type ResolvedCustomField,
  type UpdateCustomFieldInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { customerKeys } from '../customers/use-customers.js';
import {
  addCustomField,
  deleteCustomField,
  getCustomFields,
  reorderCustomFields,
  updateCustomField,
} from './custom-fields-api.js';

/**
 * A module's custom-field configuration, as the screens read it.
 *
 * Held for a long `staleTime`: this is read on every visit to the module's tab but changes
 * about once a year, and re-fetching a field list on every mount would be a request nobody
 * needs. Every mutation invalidates it AND the module's own records — hiding a field changes
 * what the sheet renders, so the rows have to be re-read alongside the columns.
 */

/** Query keys per module. */
export function customFieldKeys(module: CustomFieldModule): QueryKey {
  return ['settings', 'customFields', module] as const;
}

/** The records whose rendering a configuration change invalidates, per module. */
const RECORD_KEYS: Readonly<Record<CustomFieldModule, QueryKey>> = Object.freeze({
  customers: customerKeys.all,
});

/** Twelve hours. Long enough to be free, short enough that a second tab catches up on its own. */
const STALE_TIME = 12 * 60 * 60 * 1000;

export function useCustomFields(
  module: CustomFieldModule,
): UseQueryResult<CustomFieldConfiguration> {
  const client = useApiClient();
  return useQuery({
    queryKey: customFieldKeys(module),
    queryFn: () => getCustomFields(client, module),
    staleTime: STALE_TIME,
  });
}

/**
 * The fields with their labels already resolved.
 *
 * A built-in with no override resolves through i18n (the registry's prefix, e.g. `customers.column.`) rather than through the English literal the API also sends, so the
 * default names still translate when a second language ships. An override is the shop's own
 * words and is shown exactly as typed.
 *
 * Falls back to the module's canonical built-ins while the request is in flight, so the sheet
 * renders its usual columns on first paint instead of flashing empty.
 */
export function useResolvedCustomFields(module: CustomFieldModule): {
  fields: ResolvedCustomField[];
  labelOf: (field: ResolvedCustomField) => string;
  isPending: boolean;
} {
  const { t } = useTranslation();
  const query = useCustomFields(module);
  const definition = CUSTOM_FIELD_MODULE_DEFINITIONS[module];

  const fields = useMemo<ResolvedCustomField[]>(() => {
    if (query.data) return query.data.fields;
    return definition.builtinFields.map((field, index) => ({
      key: field.key,
      origin: 'builtin' as const,
      label: null,
      defaultLabel: field.defaultLabel,
      hidden: false,
      showInTable: field.showInTableByDefault,
      sortOrder: index,
      type: null,
      options: [],
      helperText: null,
      required: false,
      canHide: !field.essential,
      renamed: false,
      inForm: field.inForm,
      identity: field.identity,
    }));
  }, [query.data, definition]);

  const labelOf = useMemo(
    () =>
      (field: ResolvedCustomField): string => {
        if (field.label) return field.label;
        // A custom field always carries its own label; only a built-in has a translation.
        return field.origin === 'builtin'
          ? t(`${definition.i18nPrefix}${field.key}`)
          : field.defaultLabel;
      },
    [t, definition],
  );

  return { fields, labelOf, isPending: query.isPending };
}

function useConfigurationMutation<TInput>(
  module: CustomFieldModule,
  run: (input: TInput) => Promise<CustomFieldConfiguration>,
) {
  const queryClient = useQueryClient();
  return useMutation<CustomFieldConfiguration, unknown, TInput>({
    mutationFn: run,
    onSuccess: (configuration) => {
      queryClient.setQueryData(customFieldKeys(module), configuration);
      // The rows too: a hidden field is a column that has to leave the sheet.
      void queryClient.invalidateQueries({ queryKey: RECORD_KEYS[module] });
    },
  });
}

export function useAddCustomField(module: CustomFieldModule) {
  const client = useApiClient();
  return useConfigurationMutation<AddCustomFieldInput>(module, (input) =>
    addCustomField(client, module, input),
  );
}

export function useUpdateCustomField(module: CustomFieldModule) {
  const client = useApiClient();
  return useConfigurationMutation<{ fieldKey: string; input: UpdateCustomFieldInput }>(
    module,
    ({ fieldKey, input }) => updateCustomField(client, module, fieldKey, input),
  );
}

export function useReorderCustomFields(module: CustomFieldModule) {
  const client = useApiClient();
  return useConfigurationMutation<ReorderCustomFieldsInput>(module, (input) =>
    reorderCustomFields(client, module, input),
  );
}

export function useDeleteCustomField(module: CustomFieldModule) {
  const client = useApiClient();
  return useConfigurationMutation<string>(module, (fieldKey) =>
    deleteCustomField(client, module, fieldKey),
  );
}
